import { AccessCategoryMembershipInfo } from '@gym-src/API';

export type MembershipSelectItem = {
  label: string;
  program: string;
  scheduleType: string;
  value: number;
};

export type MembershipFilterItem = {
  label: string;
  value: string;
};

export type MembershipFilters = {
  programs: string[];
  scheduleTypes: string[];
};

export type MembershipFilterField = keyof MembershipFilters;

const getMembershipFilterValue = (
  membershipItem: MembershipSelectItem,
  field: MembershipFilterField
): string => (field === 'programs' ? membershipItem.program : membershipItem.scheduleType);

export const toUniqueMembershipItems = (
  membershipItems: AccessCategoryMembershipInfo[]
): MembershipSelectItem[] => {
  return Array.from(
    new Map(
      membershipItems
        .filter((membershipItem) => membershipItem && membershipItem.membershipId != null)
        .map((membershipItem) => {
          const membershipName = membershipItem.membershipName || '';
          const importedId = membershipItem.importedId ?? '';
          const program = membershipItem.program || '';
          const scheduleType = membershipItem.scheduleType || '';

          return {
            label: `${membershipName} (${importedId}) • ${program} [${scheduleType}]`,
            value: Number(membershipItem.membershipId),
            program,
            scheduleType
          };
        })
        .map((membershipItem) => [String(membershipItem.value), membershipItem])
    ).values()
  );
};

export const toMembershipFilterItems = (
  membershipItems: MembershipSelectItem[],
  field: MembershipFilterField
): MembershipFilterItem[] => {
  return Array.from(
    new Set(
      membershipItems
        .map((membershipItem) => getMembershipFilterValue(membershipItem, field))
        .filter((filterValue) => filterValue)
    )
  )
    .sort()
    .map((filterValue) => ({
      label: filterValue,
      value: filterValue
    }));
};

export const filterMembershipItems = (
  membershipItems: MembershipSelectItem[],
  filters: MembershipFilters
): MembershipSelectItem[] => {
  return membershipItems.filter(
    (membershipItem) =>
      (filters.programs.length === 0 || filters.programs.includes(membershipItem.program)) &&
      (filters.scheduleTypes.length === 0 ||
        filters.scheduleTypes.includes(membershipItem.scheduleType))
  );
};

export const includeSelectedMembershipItems = (
  membershipItems: MembershipSelectItem[],
  selectedMembershipItems: MembershipSelectItem[],
  selectedMembershipIds: number[]
): MembershipSelectItem[] => {
  const selectedIds = new Set(selectedMembershipIds.map((membershipId) => String(membershipId)));
  const selectedItems = selectedMembershipItems.filter((membershipItem) =>
    selectedIds.has(String(membershipItem.value))
  );

  return Array.from(
    new Map(
      [...membershipItems, ...selectedItems].map((membershipItem) => [
        String(membershipItem.value),
        membershipItem
      ])
    ).values()
  );
};

export const retainAvailableFilterValues = (
  filterItems: Array<{ value?: string | number }>,
  selectedValues: string[]
): string[] => {
  const availableValues = new Set(filterItems.map((filterItem) => String(filterItem.value)));

  return selectedValues.filter((selectedValue) => availableValues.has(selectedValue));
};

export const parseMembershipFilterValues = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  return Array.from(
    new Set(
      values
        .filter(
          (value): value is string | number =>
            (typeof value === 'string' && value !== '') || typeof value === 'number'
        )
        .map((value) => String(value))
    )
  );
};
